// freePlanNavigation — New Front's `buildFreePlanNavigation` (ORP-26799), re-expressed over the
// typed contribution model instead of a second hand-written menu.
//
// The Free sidebar is a FIXED list: building it from whatever the workspace's reports, flags and
// integrations unlock left a Free user seeing almost nothing they could upgrade to. It mirrors a
// fully featured paid workspace, minus Approvals.
//
// Nothing here re-declares a leaf: every node is looked up by `navKey` in the composed `app` tree,
// so labels, icons and targets stay owned by the contributing feature. Only the visibility is
// rewritten —
//   • a leaf whose route Free locks gets the `static` control, so it always shows and the route
//     gate renders the locked screen on click;
//   • a leaf Free can open keeps its own rule, since the user really lands on it;
//   • every group is `static`, so it never collapses because its report is unavailable.
// A navKey the `app` tree does not carry (a feature not registered yet) is skipped, not invented.
import type { NavItem } from './types'

interface FreePlanGroupSpec {
  navKey: string
  /** Extra hide rule the Free menu puts on the group or leaf (New Front parity). */
  hidePermission?: string
  children: readonly FreePlanLeafSpec[]
}

type FreePlanLeafSpec = string | { navKey: string; hidePermission: string }
type FreePlanEntrySpec = FreePlanLeafSpec | FreePlanGroupSpec

const FREE_PLAN_NAVIGATION: readonly FreePlanEntrySpec[] = [
  'getStarted',
  'summary',
  // Never locked: the MCP has its own free tier (ORP-27467).
  'orphexMcp',
  'orpheus',
  { navKey: 'cockpit', children: ['insights', 'alerts', 'anomalies', 'healthChecks'] },
  {
    navKey: 'workflows',
    children: [
      'workflow',
      { navKey: 'impactAnalysis', hidePermission: 'hide_causal_impact' },
      'kpiTracking',
    ],
  },
  {
    navKey: 'creativePerformanceGroup',
    children: ['creativeDashboard', 'creativeAi', 'creativeWatchlist', 'benchmark'],
  },
  {
    navKey: 'budgetPerformanceGroup',
    hidePermission: 'hide_report_6',
    children: ['budgetAllocation', 'performanceOverview', 'appAcquisition'],
  },
  {
    navKey: 'marketingInsightsGroup',
    children: [
      'performanceMax',
      'landingPage',
      'placementAnalysis',
      'searchAdsExcellence',
      'displayVideo360',
    ],
  },
  {
    navKey: 'audienceGroup',
    children: ['demographicAnalysis', 'funnelExploration', 'rfmClustering'],
  },
  {
    navKey: 'ecommerceGroup',
    children: ['productPerformance', 'customerIntelligence', 'productInsights'],
  },
  'dashboards',
  'accountCenter',
  'dataExport',
  'workspaceSettings',
]

function indexByNavKey(items: readonly NavItem[], index = new Map<string, NavItem>()) {
  for (const item of items) {
    if (item.navKey && !index.has(item.navKey)) index.set(item.navKey, item)
    if (item.children) indexByNavKey(item.children, index)
  }
  return index
}

function isGroupSpec(spec: FreePlanEntrySpec): spec is FreePlanGroupSpec {
  return typeof spec === 'object' && 'children' in spec
}

/**
 * The Free sidebar, derived from the composed paid (`app`) tree. `isLocked` is the route gate's
 * own predicate, so the sidebar and the gate can never disagree about which leaf is locked.
 */
export function buildFreePlanNavItems(
  appItems: readonly NavItem[],
  isLocked: (routeName: string | undefined) => boolean,
): NavItem[] {
  const index = indexByNavKey(appItems)

  const leaf = (spec: FreePlanLeafSpec): NavItem | null => {
    const navKey = typeof spec === 'string' ? spec : spec.navKey
    const item = index.get(navKey)
    if (!item) return null
    const hidePermission = typeof spec === 'string' ? item.hidePermission : spec.hidePermission
    // Dynamic subtrees (dashboards, account centers) and open leaves keep their own rules.
    if (item.children || !isLocked(item.to?.name)) {
      return hidePermission === undefined ? item : { ...item, hidePermission }
    }
    return {
      ...item,
      visibility: { control: 'static' },
      ...(hidePermission === undefined ? {} : { hidePermission }),
    }
  }

  const group = (spec: FreePlanGroupSpec): NavItem | null => {
    const item = index.get(spec.navKey)
    if (!item) return null
    const children = spec.children.map(leaf).filter((child): child is NavItem => child !== null)
    if (children.length === 0) return null
    const hidePermission = spec.hidePermission ?? item.hidePermission
    return {
      ...item,
      visibility: { control: 'static' },
      ...(hidePermission === undefined ? {} : { hidePermission }),
      children,
    }
  }

  return FREE_PLAN_NAVIGATION.map((spec) => (isGroupSpec(spec) ? group(spec) : leaf(spec))).filter(
    (item): item is NavItem => item !== null,
  )
}
